import { useState, type FormEvent, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { useHasPermission } from '@/auth/auth-store';
import { useT } from '@/i18n/use-t';
import { ManagedComposition } from './managed-composition';
import { AdvancedHardwarePanel } from './advanced-hardware';
import { CompositionDriftPanel } from './composition-drift-panel';
import {
  useCreateHierarchyLevel,
  useCreatePhysicalNode,
  useCreateProvisionGroup,
  useCreateProvisionHardware,
  useCreateProvisionContentAssignment,
  useCreateProvisionContentDefinition,
  useCreateProvisionRollback,
  useCreateProvisionComponentRollout,
  useConfirmProvisionComponentRolloutStep,
  useCreateProvisionRollout,
  useCreateProvisionTemplate,
  useCreateTemplateDraft,
  useDesiredStatePreview,
  useProvisionDrift,
  useProvisionFleet,
  useProvisionGroupMembership,
  useProvisionGroups,
  useProvisionHardware,
  useProvisionTemplates,
  useProvisionTopology,
  useAssignProvisionHardware,
  useMutateProvisionHardwareLifecycle,
  useMutateProvisionRollout,
  useDecideProvisionArtifact,
  usePreviewProvisionRollback,
  usePreviewProvisionComponentRollout,
  usePreviewProvisionRollout,
  useProvisionArtifacts,
  useProvisionContentDefinitions,
  useProvisionRollouts,
  useProvisionSoftwareCatalogue,
  useProvisionTargets,
  usePublishTemplateVersion,
  useTemplatePublicationPreview,
  type ProvisionEndpoint,
  type ProvisionHardware,
  type ProvisionLevel,
  type ProvisionNode,
  type CompositionEvidenceState,
  type DriftItem,
  type ProvisionDriftReport,
} from '@/hooks/use-provision';
import { certificationLabel } from './certification-label';

type View =
  | 'overview'
  | 'fleet'
  | 'topology'
  | 'groups'
  | 'templates'
  | 'composition'
  | 'hardware'
  | 'content'
  | 'releases'
  | 'rollouts';
const views: Array<{ id: View; label: string; labelKey: string }> = [
  { id: 'overview', label: 'Overview', labelKey: 'provision.nav.overview' },
  { id: 'fleet', label: 'Fleet', labelKey: 'provision.nav.fleet' },
  { id: 'topology', label: 'Topology', labelKey: 'provision.nav.topology' },
  { id: 'groups', label: 'Groups', labelKey: 'provision.nav.groups' },
  { id: 'templates', label: 'Templates', labelKey: 'provision.nav.templates' },
  { id: 'composition', label: 'Composition', labelKey: 'provision.nav.composition' },
  { id: 'hardware', label: 'Hardware', labelKey: 'provision.nav.hardware' },
  { id: 'content', label: 'Content assignments', labelKey: 'provision.nav.content' },
  { id: 'releases', label: 'Releases', labelKey: 'provision.nav.releases' },
  { id: 'rollouts', label: 'Rollouts', labelKey: 'provision.nav.rollouts' },
];

export function ProvisionPage() {
  const t = useT('admin_ui');
  const canRead = useHasPermission('provision.read');
  const canTechnician = useHasPermission('provision.technician');
  if (canTechnician && !canRead) return <TechnicianProvisionPage />;
  if (!canRead) {
    return (
      <div className="rounded-lg border p-5">
        <h1 className="text-xl font-semibold">
          {t('provision.access.title', 'Provision access required')}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t(
            'provision.access.help',
            'Ask an administrator for a Provision role or a field-technician endpoint assignment.',
          )}
        </p>
      </div>
    );
  }
  return <AdminProvisionPage />;
}

function TechnicianProvisionPage() {
  const t = useT('admin_ui');
  const fleet = useProvisionFleet('technician');
  const drift = useProvisionDrift({}, 'technician');
  return (
    <div className="space-y-5">
      <header>
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-2xl font-semibold">
            {t('provision.technician.title', 'Peripheral setup')}
          </h1>
          <Badge variant="outline">{t('provision.technician.badge', 'Field technician')}</Badge>
        </div>
        <p className="text-sm text-muted-foreground">
          {t(
            'provision.technician.intro',
            'Configure published equipment only on terminals assigned to you. Adapter identities and compatible versions are resolved automatically.',
          )}
        </p>
      </header>
      {fleet.isLoading ? (
        <div className="rounded-lg border p-4 text-sm text-muted-foreground">
          {t('provision.technician.loading', 'Loading assigned terminals…')}
        </div>
      ) : (fleet.data?.length ?? 0) === 0 ? (
        <div className="rounded-lg border p-4 text-sm">
          {t(
            'provision.technician.empty',
            'No terminals are assigned. An administrator must assign an endpoint before setup can begin.',
          )}
        </div>
      ) : (
        <ManagedComposition
          endpoints={fleet.data ?? []}
          drift={drift.data?.items ?? []}
          accessMode="technician"
        />
      )}
    </div>
  );
}

function AdminProvisionPage() {
  const t = useT('admin_ui');
  const [params, setParams] = useSearchParams();
  const requested = params.get('view');
  const view = views.some((candidate) => candidate.id === requested)
    ? (requested as View)
    : 'overview';
  const topology = useProvisionTopology();
  const fleet = useProvisionFleet();
  const groups = useProvisionGroups();
  const drift = useProvisionDrift();
  const templates = useProvisionTemplates();
  const hardware = useProvisionHardware();
  const targets = useProvisionTargets();
  const contentDefinitions = useProvisionContentDefinitions();
  const artifacts = useProvisionArtifacts();
  const rollouts = useProvisionRollouts();
  const softwareCatalogue = useProvisionSoftwareCatalogue(null, { enabled: view === 'rollouts' });

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-2xl font-semibold">{t('provision.page.title', 'Provision')}</h1>
        <p className="text-sm text-muted-foreground">
          {t(
            'provision.page.subtitle',
            'Desired-state ownership, estate placement, operational targeting, and convergence.',
          )}
        </p>
      </header>
      <nav
        aria-label={t('provision.nav.ariaLabel', 'Provision views')}
        className="flex flex-wrap gap-2 border-b pb-3"
      >
        {views.map((item) => (
          <Button
            key={item.id}
            size="sm"
            variant={view === item.id ? 'default' : 'ghost'}
            onClick={() => setParams(item.id === 'overview' ? {} : { view: item.id })}
          >
            {t(item.labelKey, item.label)}
          </Button>
        ))}
      </nav>
      {view === 'overview' && (
        <Overview
          endpoints={fleet.data ?? []}
          nodes={topology.data?.nodes ?? []}
          groups={groups.data ?? []}
          drift={drift.data}
        />
      )}
      {view === 'fleet' && <Fleet endpoints={fleet.data ?? []} drift={drift.data?.items ?? []} />}
      {view === 'topology' && (
        <Topology levels={topology.data?.levels ?? []} nodes={topology.data?.nodes ?? []} />
      )}
      {view === 'groups' && <Groups groups={groups.data ?? []} endpoints={fleet.data ?? []} />}
      {view === 'templates' && <Templates templates={templates.data ?? []} />}
      {view === 'composition' && (
        <ManagedComposition
          endpoints={fleet.data ?? []}
          drift={drift.data?.items ?? []}
          onOpenRollouts={() => setParams({ view: 'rollouts' })}
        />
      )}
      {view === 'hardware' && (
        <Hardware devices={hardware.data ?? []} endpoints={fleet.data ?? []} />
      )}
      {view === 'content' && (
        <ContentAssignments
          definitions={contentDefinitions.data ?? []}
          targets={targets.data ?? []}
        />
      )}
      {view === 'releases' && <Releases artifacts={artifacts.data ?? []} />}
      {view === 'rollouts' && (
        <Rollouts
          rollouts={rollouts.data ?? []}
          artifacts={artifacts.data ?? []}
          targets={targets.data ?? []}
          components={softwareCatalogue.data ?? []}
        />
      )}
    </div>
  );
}

function ContentAssignments({
  definitions,
  targets,
}: {
  definitions: Array<{
    id: string;
    kind: 'assortment' | 'hotkey_layout';
    name: string;
    isDefault: boolean;
    versions: Array<{ id: string; version: number; state: string }>;
  }>;
  targets: Array<{
    id: string;
    label: string;
    contentAssignments: Array<{
      id: string;
      kind: string;
      contentDefinitionId: string | null;
      countryPackId: string | null;
      followLatest: boolean;
    }>;
  }>;
}) {
  const t = useT('admin_ui');
  const canAdmin = useHasPermission('provision.admin');
  const canWrite = useHasPermission('provision.write');
  const create = useCreateProvisionContentDefinition();
  const assign = useCreateProvisionContentAssignment();
  const [definition, setDefinition] = useState({
    kind: 'assortment' as 'assortment' | 'hotkey_layout',
    name: '',
  });
  const [assignment, setAssignment] = useState({ targetId: '', definitionId: '' });
  const selected = definitions.find((item) => item.id === assignment.definitionId);
  return (
    <div className="space-y-4">
      <div className="rounded-md border">
        <Table>
          <THead>
            <TR>
              <TH>{t('provision.content.target', 'Target')}</TH>
              <TH>{t('provision.content.effectiveAssignments', 'Effective assignments')}</TH>
            </TR>
          </THead>
          <TBody>
            {targets.map((target) => (
              <TR key={target.id}>
                <TD className="font-medium">{target.label}</TD>
                <TD className="space-x-2">
                  {target.contentAssignments.map((item) => (
                    <Badge key={item.id} variant="outline">
                      {item.kind}:{' '}
                      {definitions.find((entry) => entry.id === item.contentDefinitionId)?.name ??
                        item.countryPackId ??
                        t('provision.content.missing', 'missing')}{' '}
                      ·{' '}
                      {item.followLatest
                        ? t('provision.content.latest', 'latest')
                        : t('provision.content.pinned', 'pinned')}
                    </Badge>
                  ))}
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        {canAdmin && (
          <form
            className="space-y-2 rounded-lg border p-4"
            onSubmit={(event) => {
              event.preventDefault();
              void create
                .mutateAsync({
                  ...definition,
                  payload:
                    definition.kind === 'assortment'
                      ? { mode: 'selected', itemIds: [] }
                      : { nodes: [] },
                })
                .then(() => setDefinition({ ...definition, name: '' }));
            }}
          >
            <h2 className="font-medium">
              {t('provision.content.createStream', 'Create Catalog-owned version stream')}
            </h2>
            <Select
              value={definition.kind}
              onChange={(event) =>
                setDefinition({ ...definition, kind: event.target.value as typeof definition.kind })
              }
            >
              <option value="assortment">{t('provision.content.assortment', 'Assortment')}</option>
              <option value="hotkey_layout">
                {t('provision.content.hotkeyLayout', 'Hotkey layout')}
              </option>
            </Select>
            <Input
              required
              placeholder={t('provision.field.name', 'Name')}
              value={definition.name}
              onChange={(event) => setDefinition({ ...definition, name: event.target.value })}
            />
            <Button size="sm">{t('provision.action.createDraftV1', 'Create draft v1')}</Button>
          </form>
        )}
        {canWrite && (
          <form
            className="space-y-2 rounded-lg border p-4"
            onSubmit={(event) => {
              event.preventDefault();
              if (selected !== undefined)
                void assign.mutateAsync({
                  kind: selected.kind,
                  targetId: assignment.targetId,
                  contentDefinitionId: selected.id,
                  followLatest: true,
                  priority: 0,
                });
            }}
          >
            <h2 className="font-medium">
              {t('provision.content.assignTitle', 'Assign published content')}
            </h2>
            <Select
              required
              value={assignment.targetId}
              onChange={(event) => setAssignment({ ...assignment, targetId: event.target.value })}
            >
              <option value="">{t('provision.content.selectTarget', 'Select target')}</option>
              {targets.map((target) => (
                <option key={target.id} value={target.id}>
                  {target.label}
                </option>
              ))}
            </Select>
            <Select
              required
              value={assignment.definitionId}
              onChange={(event) =>
                setAssignment({ ...assignment, definitionId: event.target.value })
              }
            >
              <option value="">
                {t('provision.content.selectDefinition', 'Select assortment or layout')}
              </option>
              {definitions
                .filter((item) => item.versions.some((version) => version.state === 'published'))
                .map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.kind}: {item.name}
                  </option>
                ))}
            </Select>
            <Button size="sm">
              {t('provision.content.assignFollowLatest', 'Assign follow-latest')}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}

function Releases({
  artifacts,
}: {
  artifacts: Array<{
    id: string;
    kind: 'core' | 'ui';
    applicationType: string;
    semanticVersion: string;
    platform: string;
    architecture: string;
    sha256: string;
    status: string;
    coreVersionRange: string | null;
    uiPortVersionRange: string | null;
    approvals: Array<{ status: 'approved' | 'revoked'; reason: string; decidedAt: string }>;
  }>;
}) {
  const t = useT('admin_ui');
  const canAdmin = useHasPermission('provision.admin');
  const decide = useDecideProvisionArtifact();
  const [reason, setReason] = useState('Reviewed release evidence');
  return (
    <div className="space-y-4">
      <div className="rounded-lg border bg-card p-4">
        <h2 className="font-medium">{t('provision.releases.trustTitle', 'Trust boundary')}</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {t(
            'provision.releases.trustHelp',
            'Core and UI metadata arrives only from the signed release pipeline. Tenant admins can approve retailer UI metadata, never upload core binaries.',
          )}
        </p>
        {canAdmin && (
          <Input
            className="mt-3 max-w-xl"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            aria-label={t('provision.releases.reasonLabel', 'Artifact decision reason')}
          />
        )}
      </div>
      <div className="rounded-md border">
        <Table>
          <THead>
            <TR>
              <TH>{t('provision.releases.colArtifact', 'Artifact')}</TH>
              <TH>{t('provision.releases.colRuntime', 'Runtime')}</TH>
              <TH>{t('provision.releases.colIntegrity', 'Integrity / compatibility')}</TH>
              <TH>{t('provision.releases.colApproval', 'Approval')}</TH>
              <TH />
            </TR>
          </THead>
          <TBody>
            {artifacts.map((artifact) => {
              const latest = artifact.approvals[0];
              return (
                <TR key={artifact.id}>
                  <TD>
                    <div className="font-medium">
                      {artifact.kind} {artifact.semanticVersion}
                    </div>
                    <Badge variant="outline">{artifact.applicationType}</Badge>
                  </TD>
                  <TD>
                    {artifact.platform} / {artifact.architecture}
                  </TD>
                  <TD>
                    <code className="text-xs">{artifact.sha256.slice(0, 12)}…</code>
                    {artifact.kind === 'ui' && (
                      <div className="text-xs text-muted-foreground">
                        core {artifact.coreVersionRange}
                        <br />
                        UI port {artifact.uiPortVersionRange}
                      </div>
                    )}
                  </TD>
                  <TD>
                    <Badge
                      variant={
                        artifact.status !== 'published' || latest?.status === 'revoked'
                          ? 'destructive'
                          : artifact.kind === 'core' || latest?.status === 'approved'
                            ? 'success'
                            : 'outline'
                      }
                    >
                      {artifact.status !== 'published'
                        ? artifact.status
                        : artifact.kind === 'core'
                          ? t('provision.releases.pipelineTrusted', 'pipeline trusted')
                          : (latest?.status ??
                            t('provision.releases.pendingApproval', 'pending approval'))}
                    </Badge>
                  </TD>
                  <TD className="space-x-1 text-right">
                    {canAdmin && artifact.kind === 'ui' && artifact.status === 'published' && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            decide.mutate({ artifactId: artifact.id, action: 'approve', reason })
                          }
                        >
                          {t('provision.releases.approve', 'Approve')}
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() =>
                            decide.mutate({ artifactId: artifact.id, action: 'revoke', reason })
                          }
                        >
                          {t('provision.releases.revoke', 'Revoke')}
                        </Button>
                      </>
                    )}
                  </TD>
                </TR>
              );
            })}
          </TBody>
        </Table>
      </div>
    </div>
  );
}

function Rollouts({
  rollouts,
  artifacts,
  targets,
  components,
}: {
  rollouts: Array<{
    id: string;
    name: string;
    coreArtifactId: string | null;
    uiArtifactId: string | null;
    kind: 'legacy_artifact_pair' | 'component_plan';
    componentPlan: Record<string, unknown> | null;
    state: string;
    reason: string;
    rollbackOfId: string | null;
    stages: Array<{
      id: string;
      position: number;
      name: string;
      pilot: boolean;
      state: string;
      targets: Array<{
        id: string;
        applicationInstanceId: string;
        state: string;
        lastErrorCode: string | null;
        componentPlan: {
          steps: Array<{ stepId: string; action: string; executor: string; dependsOn: string[] }>;
        } | null;
        componentProgress: { completedStepIds: string[] } | null;
      }>;
    }>;
  }>;
  artifacts: Array<{
    id: string;
    kind: 'core' | 'ui';
    semanticVersion: string;
    applicationType: string;
    status: string;
    approvals: Array<{ status: string }>;
  }>;
  targets: Array<{ id: string; label: string }>;
  components: Array<{
    componentId: string;
    key: string;
    displayName: string;
    componentType: string;
    versions: Array<{
      componentVersionId: string;
      semanticVersion: string;
      state: string;
      deliveryMode: string;
      trusted: boolean;
      tenantApproved: boolean;
      approvalStatus: 'approved' | 'revoked' | null;
      certification?: 'certified' | 'test';
    }>;
  }>;
}) {
  const t = useT('admin_ui');
  const canWrite = useHasPermission('provision.write');
  const canAdmin = useHasPermission('provision.admin');
  const preview = usePreviewProvisionRollout();
  const create = useCreateProvisionRollout();
  const mutate = useMutateProvisionRollout();
  const rollbackPreview = usePreviewProvisionRollback();
  const rollback = useCreateProvisionRollback();
  const componentPreview = usePreviewProvisionComponentRollout();
  const createComponents = useCreateProvisionComponentRollout();
  const confirmComponentStep = useConfirmProvisionComponentRolloutStep();
  const [form, setForm] = useState({
    name: '',
    coreArtifactId: '',
    uiArtifactId: '',
    targetId: '',
    reason: '',
    confirmation: '',
  });
  const [rollbackConfirmation, setRollbackConfirmation] = useState('');
  const [componentForm, setComponentForm] = useState({
    name: '',
    componentVersionIds: [] as string[],
    targetId: '',
    reason: '',
    confirmation: '',
  });
  const [operatorReasons, setOperatorReasons] = useState<Record<string, string>>({});
  const runPreview = () =>
    preview.mutate({
      coreArtifactId: form.coreArtifactId,
      uiArtifactId: form.uiArtifactId,
      targetIds: [form.targetId],
    });
  const rolloutVersions = components.flatMap((component) =>
    component.versions
      .filter(
        (version) =>
          version.state === 'published' &&
          version.approvalStatus !== 'revoked' &&
          (version.deliveryMode !== 'provision_artifact' ||
            (version.trusted && version.tenantApproved)),
      )
      .map((version) => ({ ...version, component })),
  );
  return (
    <div className="space-y-4">
      {canAdmin && (
        <section className="rounded-lg border p-4">
          <h2 className="font-medium">
            {t('provision.rollouts.artifactTitle', 'Core/UI artifact rollout')}
          </h2>
          <div className="mt-3 grid gap-2 md:grid-cols-2 xl:grid-cols-4">
            <Input
              required
              placeholder={t('provision.rollouts.namePlaceholder', 'Rollout name')}
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
            />
            <Select
              required
              value={form.coreArtifactId}
              onChange={(event) => setForm({ ...form, coreArtifactId: event.target.value })}
            >
              <option value="">{t('provision.rollouts.coreRelease', 'Core release')}</option>
              {artifacts
                .filter((item) => item.kind === 'core' && item.status === 'published')
                .map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.applicationType} core {item.semanticVersion}
                  </option>
                ))}
            </Select>
            <Select
              required
              value={form.uiArtifactId}
              onChange={(event) => setForm({ ...form, uiArtifactId: event.target.value })}
            >
              <option value="">
                {t('provision.rollouts.approvedUiRelease', 'Approved UI release')}
              </option>
              {artifacts
                .filter(
                  (item) =>
                    item.kind === 'ui' &&
                    item.status === 'published' &&
                    item.approvals[0]?.status === 'approved',
                )
                .map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.applicationType} UI {item.semanticVersion}
                  </option>
                ))}
            </Select>
            <Select
              required
              value={form.targetId}
              onChange={(event) => setForm({ ...form, targetId: event.target.value })}
            >
              <option value="">{t('provision.rollouts.targetSelector', 'Target selector')}</option>
              {targets.map((target) => (
                <option key={target.id} value={target.id}>
                  {target.label}
                </option>
              ))}
            </Select>
            <Input
              className="md:col-span-2"
              placeholder={t('provision.rollouts.operatorReason', 'Operator reason')}
              value={form.reason}
              onChange={(event) => setForm({ ...form, reason: event.target.value })}
            />
            <Button
              variant="outline"
              disabled={!form.coreArtifactId || !form.uiArtifactId || !form.targetId}
              onClick={runPreview}
            >
              {t('provision.rollouts.previewBlastRadius', 'Preview blast radius')}
            </Button>
          </div>
          {preview.data !== undefined && (
            <div className="mt-4 rounded-md bg-secondary/40 p-3">
              <div className="flex flex-wrap gap-2">
                {Object.entries(preview.data.counts).map(([key, value]) => (
                  <Badge key={key} variant="outline">
                    {key.replaceAll('_', ' ')}: {value}
                  </Badge>
                ))}
              </div>
              <p className="mt-2 text-sm">
                {t('provision.rollouts.frozenApplications', 'Frozen applications:')}{' '}
                {preview.data.applicationIds.join(', ') || t('provision.rollouts.none', 'none')}.{' '}
                {t('provision.rollouts.pilotNote', 'The first application is the explicit pilot.')}
              </p>
              {preview.data.confirmationPhrase !== null && (
                <Input
                  className="mt-2 max-w-md"
                  placeholder={preview.data.confirmationPhrase}
                  value={form.confirmation}
                  onChange={(event) => setForm({ ...form, confirmation: event.target.value })}
                />
              )}
              <Button
                className="mt-3"
                size="sm"
                disabled={
                  !form.name ||
                  !form.reason ||
                  preview.data.applicationIds.length === 0 ||
                  (preview.data.confirmationPhrase !== null &&
                    form.confirmation !== preview.data.confirmationPhrase)
                }
                onClick={() =>
                  create.mutate({
                    name: form.name,
                    coreArtifactId: form.coreArtifactId,
                    uiArtifactId: form.uiArtifactId,
                    targetIds: [form.targetId],
                    pilotApplicationIds: preview.data?.applicationIds.slice(0, 1) ?? [],
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    downloadConcurrencyStore: 2,
                    installConcurrencyStore: 1,
                    observationMinutes: 30,
                    reason: form.reason,
                    previewChecksum: preview.data?.previewChecksum ?? '',
                    ...(preview.data?.confirmationPhrase === null
                      ? {}
                      : { confirmationPhrase: form.confirmation }),
                  })
                }
              >
                {t('provision.rollouts.create', 'Create rollout')}
              </Button>
            </div>
          )}
        </section>
      )}
      {canAdmin && (
        <section className="rounded-lg border p-4">
          <h2 className="font-medium">
            {t('provision.rollouts.componentTitle', 'Catalogue component rollout')}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {t(
              'provision.rollouts.componentHelp',
              'Select desired published releases. Provision derives artifact, delegated, bundled, and operator steps in dependency order and captures a rollback point per application.',
            )}
          </p>
          <div className="mt-3 grid gap-2 md:grid-cols-2 xl:grid-cols-4">
            <Input
              required
              placeholder={t(
                'provision.rollouts.componentNamePlaceholder',
                'Component rollout name',
              )}
              value={componentForm.name}
              onChange={(event) => setComponentForm({ ...componentForm, name: event.target.value })}
            />
            <Select
              required
              value={componentForm.targetId}
              onChange={(event) =>
                setComponentForm({ ...componentForm, targetId: event.target.value })
              }
            >
              <option value="">{t('provision.rollouts.targetSelector', 'Target selector')}</option>
              {targets.map((target) => (
                <option key={target.id} value={target.id}>
                  {target.label}
                </option>
              ))}
            </Select>
            <Select
              multiple
              className="h-36 md:col-span-2"
              aria-label={t('provision.rollouts.componentReleasesLabel', 'Component releases')}
              value={componentForm.componentVersionIds}
              onChange={(event) =>
                setComponentForm({
                  ...componentForm,
                  componentVersionIds: Array.from(
                    event.currentTarget.selectedOptions,
                    (option) => option.value,
                  ),
                })
              }
            >
              {rolloutVersions.map((version) => (
                <option key={version.componentVersionId} value={version.componentVersionId}>
                  {version.component.displayName} {version.semanticVersion} ·{' '}
                  {version.component.componentType} · {version.deliveryMode.replaceAll('_', ' ')} ·{' '}
                  {certificationLabel(version.certification, t)}
                </option>
              ))}
            </Select>
            <Input
              className="md:col-span-2"
              placeholder={t('provision.rollouts.operatorReason', 'Operator reason')}
              value={componentForm.reason}
              onChange={(event) =>
                setComponentForm({ ...componentForm, reason: event.target.value })
              }
            />
            <Button
              variant="outline"
              disabled={!componentForm.targetId || componentForm.componentVersionIds.length === 0}
              onClick={() =>
                componentPreview.mutate({
                  targetIds: [componentForm.targetId],
                  componentVersionIds: componentForm.componentVersionIds,
                })
              }
            >
              {t('provision.rollouts.previewComponentPlan', 'Preview component plan')}
            </Button>
          </div>
          {componentPreview.data !== undefined && (
            <div className="mt-4 space-y-3 rounded-md bg-secondary/40 p-3">
              <div className="flex flex-wrap gap-2">
                {Object.entries(componentPreview.data.counts).map(([key, value]) => (
                  <Badge key={key} variant="outline">
                    {key.replaceAll('_', ' ')}: {value}
                  </Badge>
                ))}
              </div>
              {componentPreview.data.plans.map((plan) => (
                <div key={plan.applicationInstanceId} className="rounded border bg-background p-2">
                  <strong className="text-sm">{plan.applicationInstanceId}</strong>
                  <p className="text-xs text-muted-foreground">
                    {plan.steps.length} {t('provision.rollouts.orderedSteps', 'ordered steps')} ·{' '}
                    {plan.strictestActivationPolicy.replaceAll('_', ' ')}
                    {plan.hasOperatorSteps
                      ? ` · ${t('provision.rollouts.operatorCheckpoint', 'operator checkpoint')}`
                      : ''}{' '}
                    · {plan.rollbackPoints.length}{' '}
                    {t('provision.rollouts.rollbackPoints', 'rollback point(s)')}
                  </p>
                </div>
              ))}
              {componentPreview.data.incompatibleApplications.map((application) => (
                <p key={application.applicationInstanceId} className="text-sm text-destructive">
                  {application.applicationInstanceId}: {application.reason}
                </p>
              ))}
              {componentPreview.data.confirmationPhrase !== null && (
                <Input
                  className="max-w-md"
                  placeholder={componentPreview.data.confirmationPhrase}
                  value={componentForm.confirmation}
                  onChange={(event) =>
                    setComponentForm({ ...componentForm, confirmation: event.target.value })
                  }
                />
              )}
              <Button
                size="sm"
                disabled={
                  !componentForm.name ||
                  !componentForm.reason ||
                  componentPreview.data.applicationIds.length === 0 ||
                  (componentPreview.data.confirmationPhrase !== null &&
                    componentForm.confirmation !== componentPreview.data.confirmationPhrase)
                }
                onClick={() =>
                  createComponents.mutate({
                    name: componentForm.name,
                    targetIds: [componentForm.targetId],
                    componentVersionIds: componentForm.componentVersionIds,
                    pilotApplicationIds: componentPreview.data?.applicationIds.slice(0, 1) ?? [],
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    downloadConcurrencyStore: 2,
                    installConcurrencyStore: 1,
                    observationMinutes: 30,
                    reason: componentForm.reason,
                    previewChecksum: componentPreview.data?.previewChecksum ?? '',
                    ...(componentPreview.data?.confirmationPhrase === null
                      ? {}
                      : { confirmationPhrase: componentForm.confirmation }),
                  })
                }
              >
                {t('provision.rollouts.createComponent', 'Create component rollout')}
              </Button>
            </div>
          )}
        </section>
      )}
      {rollouts.map((rollout) => (
        <section key={rollout.id} className="rounded-lg border p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="font-medium">{rollout.name}</h2>
              <p className="text-sm text-muted-foreground">
                {rollout.reason}
                {rollout.rollbackOfId !== null &&
                  ` · ${t('provision.rollouts.rollbackSuffix', 'rollback')}`}
              </p>
              <Badge className="mt-1" variant="outline">
                {rollout.kind === 'component_plan'
                  ? t('provision.rollouts.kindComponentPlan', 'component plan')
                  : t('provision.rollouts.kindArtifactPair', 'core/UI pair')}
              </Badge>
            </div>
            <div className="flex gap-1">
              <Badge
                variant={
                  rollout.state.startsWith('completed')
                    ? 'success'
                    : rollout.state === 'cancelled'
                      ? 'destructive'
                      : 'outline'
                }
              >
                {rollout.state}
              </Badge>
              {canWrite && rollout.state === 'running' && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    mutate.mutate({
                      rolloutId: rollout.id,
                      action: 'pause',
                      reason: 'Paused from rollout console',
                    })
                  }
                >
                  {t('provision.rollouts.pause', 'Pause')}
                </Button>
              )}
              {canWrite && rollout.state === 'paused' && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    mutate.mutate({
                      rolloutId: rollout.id,
                      action: 'resume',
                      reason: 'Resumed from rollout console',
                    })
                  }
                >
                  {t('provision.rollouts.resume', 'Resume')}
                </Button>
              )}
              {canAdmin && rollout.state === 'awaiting_stage_approval' && (
                <Button
                  size="sm"
                  onClick={() =>
                    mutate.mutate({
                      rolloutId: rollout.id,
                      action: 'approve-next-stage',
                      reason: 'Stage evidence reviewed',
                    })
                  }
                >
                  {t('provision.rollouts.approveNextStage', 'Approve next stage')}
                </Button>
              )}
              {canWrite &&
                !['cancelled', 'completed', 'completed_with_failures'].includes(rollout.state) && (
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() =>
                      mutate.mutate({
                        rolloutId: rollout.id,
                        action: 'cancel',
                        reason: 'Cancelled from rollout console',
                      })
                    }
                  >
                    {t('common.cancel', 'Cancel')}
                  </Button>
                )}
            </div>
          </div>
          <div className="mt-3 space-y-2">
            {rollout.stages.map((stage) => (
              <div key={stage.id} className="rounded-md bg-secondary/40 p-3">
                <div className="flex justify-between">
                  <strong>
                    {stage.position}. {stage.name}
                    {stage.pilot ? ` (${t('provision.rollouts.pilot', 'pilot')})` : ''}
                  </strong>
                  <Badge variant="outline">{stage.state}</Badge>
                </div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {stage.targets.map((target) => (
                    <div key={target.id} className="flex flex-col gap-1">
                      <Badge
                        variant={
                          target.state === 'failed'
                            ? 'destructive'
                            : target.state === 'succeeded'
                              ? 'success'
                              : 'outline'
                        }
                      >
                        {target.applicationInstanceId}: {target.state}
                        {target.lastErrorCode !== null ? ` · ${target.lastErrorCode}` : ''}
                      </Badge>
                      {target.componentPlan !== null && (
                        <span className="text-xs text-muted-foreground">
                          {target.componentProgress?.completedStepIds.length ?? 0}/
                          {target.componentPlan.steps.length}{' '}
                          {t('provision.rollouts.stepsComplete', 'steps complete')}
                        </span>
                      )}
                      {canAdmin &&
                        target.componentPlan?.steps
                          .filter(
                            (step) =>
                              step.executor === 'operator' &&
                              !(target.componentProgress?.completedStepIds ?? []).includes(
                                step.stepId,
                              ) &&
                              step.dependsOn.every((dependency) =>
                                (target.componentProgress?.completedStepIds ?? []).includes(
                                  dependency,
                                ),
                              ),
                          )
                          .map((step) => {
                            const reasonKey = `${target.id}:${step.stepId}`;
                            const reason = operatorReasons[reasonKey] ?? '';
                            return (
                              <div key={step.stepId} className="flex max-w-md gap-1">
                                <Input
                                  aria-label={`${t(
                                    'provision.rollouts.reasonFor',
                                    'Reason for',
                                  )} ${step.action.replaceAll('_', ' ')}`}
                                  placeholder={t(
                                    'provision.rollouts.adminVerificationReason',
                                    'Admin verification reason',
                                  )}
                                  value={reason}
                                  onChange={(event) =>
                                    setOperatorReasons({
                                      ...operatorReasons,
                                      [reasonKey]: event.target.value,
                                    })
                                  }
                                />
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={!reason.trim()}
                                  onClick={() =>
                                    confirmComponentStep.mutate({
                                      targetId: target.id,
                                      stepId: step.stepId,
                                      reason,
                                    })
                                  }
                                >
                                  {t('provision.rollouts.confirm', 'Confirm')}{' '}
                                  {step.action.replaceAll('_', ' ')}
                                </Button>
                              </div>
                            );
                          })}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          {canAdmin &&
            rollout.kind === 'legacy_artifact_pair' &&
            ['paused', 'completed', 'completed_with_failures'].includes(rollout.state) &&
            rollout.stages[0]?.targets[0] !== undefined &&
            artifacts.some(
              (artifact) => artifact.kind === 'core' && artifact.id !== rollout.coreArtifactId,
            ) &&
            artifacts.some(
              (artifact) =>
                artifact.kind === 'ui' &&
                artifact.id !== rollout.uiArtifactId &&
                artifact.approvals[0]?.status === 'approved',
            ) && (
              <div className="mt-3 rounded-md border p-3">
                {rollbackPreview.variables?.sourceRolloutId !== rollout.id ||
                rollbackPreview.data === undefined ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setRollbackConfirmation('');
                      rollbackPreview.mutate({
                        sourceRolloutId: rollout.id,
                        coreArtifactId:
                          artifacts.find(
                            (artifact) =>
                              artifact.kind === 'core' && artifact.id !== rollout.coreArtifactId,
                          )?.id ?? '',
                        uiArtifactId:
                          artifacts.find(
                            (artifact) =>
                              artifact.kind === 'ui' &&
                              artifact.id !== rollout.uiArtifactId &&
                              artifact.approvals[0]?.status === 'approved',
                          )?.id ?? '',
                      });
                    }}
                  >
                    {t(
                      'provision.rollouts.previewRollbackBlastRadius',
                      'Preview rollback blast radius',
                    )}
                  </Button>
                ) : (
                  <div className="space-y-2">
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(rollbackPreview.data.counts).map(([key, value]) => (
                        <Badge key={key} variant="outline">
                          {key.replaceAll('_', ' ')}: {value}
                        </Badge>
                      ))}
                    </div>
                    {rollbackPreview.data.confirmationPhrase !== null && (
                      <Input
                        className="max-w-md"
                        placeholder={rollbackPreview.data.confirmationPhrase}
                        value={rollbackConfirmation}
                        onChange={(event) => setRollbackConfirmation(event.target.value)}
                      />
                    )}
                    <Button
                      size="sm"
                      disabled={
                        rollbackPreview.data.applicationIds.length === 0 ||
                        (rollbackPreview.data.confirmationPhrase !== null &&
                          rollbackConfirmation !== rollbackPreview.data.confirmationPhrase)
                      }
                      onClick={() =>
                        rollback.mutate({
                          ...rollbackPreview.variables,
                          pilotApplicationIds: rollbackPreview.data.applicationIds.slice(0, 1),
                          reason: `Rollback requested from ${rollout.name}`,
                          previewChecksum: rollbackPreview.data.previewChecksum,
                          ...(rollbackPreview.data.confirmationPhrase === null
                            ? {}
                            : { confirmationPhrase: rollbackConfirmation }),
                        })
                      }
                    >
                      {t('provision.rollouts.createRollback', 'Create safeguarded rollback')}
                    </Button>
                  </div>
                )}
              </div>
            )}
        </section>
      ))}
    </div>
  );
}

function Templates({
  templates,
}: {
  templates: Array<{
    id: string;
    name: string;
    description: string | null;
    assignments: Array<{ id: string; followLatest: boolean }>;
    versions: Array<{
      id: string;
      version: number;
      state: string;
      rowVersion: number;
      publicationReason: string | null;
    }>;
  }>;
}) {
  const t = useT('admin_ui');
  const canAdmin = useHasPermission('provision.admin');
  const create = useCreateProvisionTemplate();
  const createDraft = useCreateTemplateDraft();
  const preview = useTemplatePublicationPreview();
  const publish = usePublishTemplateVersion();
  const [name, setName] = useState('');
  const [reason, setReason] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const draft = templates
    .flatMap((template) => template.versions)
    .find((version) => version.state === 'draft');
  return (
    <div className="grid gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(320px,1fr)]">
      <div className="space-y-3">
        {templates.map((template) => (
          <section key={template.id} className="rounded-lg border p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="font-medium">{template.name}</h2>
                <p className="text-sm text-muted-foreground">
                  {template.description ?? t('provision.templates.noDescription', 'No description')}{' '}
                  · {template.assignments.length}{' '}
                  {t('provision.templates.assignments', 'assignments')}
                </p>
              </div>
              {canAdmin && !template.versions.some((version) => version.state === 'draft') && (
                <Button size="sm" variant="outline" onClick={() => createDraft.mutate(template.id)}>
                  {t('provision.templates.newDraft', 'New draft')}
                </Button>
              )}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {template.versions.map((version) => (
                <Badge
                  key={version.id}
                  variant={version.state === 'published' ? 'success' : 'outline'}
                >
                  v{version.version} · {version.state}
                </Badge>
              ))}
            </div>
          </section>
        ))}
        {templates.length === 0 && (
          <div className="rounded-lg border p-6 text-sm text-muted-foreground">
            {t('provision.templates.empty', 'No templates yet.')}
          </div>
        )}
      </div>
      <aside className="space-y-4">
        {canAdmin && (
          <form
            className="space-y-2 rounded-lg border p-4"
            onSubmit={(event) => {
              event.preventDefault();
              void create
                .mutateAsync({ name, config: {}, activationPolicies: {} })
                .then(() => setName(''));
            }}
          >
            <h2 className="font-medium">
              {t('provision.templates.createTitle', 'Create template')}
            </h2>
            <Input
              required
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder={t('provision.templates.namePlaceholder', 'Template name')}
            />
            <Button size="sm">{t('provision.action.createDraftV1', 'Create draft v1')}</Button>
          </form>
        )}
        {canAdmin && draft !== undefined && (
          <section className="space-y-3 rounded-lg border p-4">
            <h2 className="font-medium">
              {t('provision.templates.publish', 'Publish')} v{draft.version}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t(
                'provision.templates.previewHelp',
                'Generate a bound impact preview before confirmation.',
              )}
            </p>
            <Button size="sm" variant="outline" onClick={() => preview.mutate(draft.id)}>
              {t('provision.templates.previewImpact', 'Preview impact')}
            </Button>
            {preview.data !== undefined && (
              <div className="space-y-2 text-sm">
                <div className="grid grid-cols-2 gap-1">
                  {Object.entries(preview.data.counts).map(([key, value]) => (
                    <div key={key} className="contents">
                      <span>{key.replaceAll('_', ' ')}</span>
                      <strong>{value}</strong>
                    </div>
                  ))}
                </div>
                <Input
                  required
                  placeholder={t('provision.field.reason', 'Reason')}
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                />
                {preview.data.confirmationPhrase !== null && (
                  <Input
                    required
                    placeholder={preview.data.confirmationPhrase}
                    value={confirmation}
                    onChange={(event) => setConfirmation(event.target.value)}
                  />
                )}
                <Button
                  size="sm"
                  disabled={
                    !reason ||
                    (preview.data.confirmationPhrase !== null &&
                      confirmation !== preview.data.confirmationPhrase)
                  }
                  onClick={() =>
                    publish.mutate({
                      versionId: draft.id,
                      expectedRowVersion: draft.rowVersion,
                      previewChecksum: preview.data.previewChecksum,
                      ...(preview.data.confirmationPhrase === null
                        ? {}
                        : { confirmationPhrase: confirmation }),
                      reason,
                    })
                  }
                >
                  {t('provision.templates.confirmPublication', 'Confirm publication')}
                </Button>
              </div>
            )}
          </section>
        )}
      </aside>
    </div>
  );
}

function Hardware({
  devices,
  endpoints,
}: {
  devices: ProvisionHardware[];
  endpoints: ProvisionEndpoint[];
}) {
  const t = useT('admin_ui');
  const canAdmin = useHasPermission('provision.admin');
  const create = useCreateProvisionHardware();
  const assign = useAssignProvisionHardware();
  const lifecycle = useMutateProvisionHardwareLifecycle();
  const [form, setForm] = useState({
    classKey: '',
    serialAssetId: '',
    connectionType: 'usb',
    adapterId: '',
    secretReference: '',
  });
  const [binding, setBinding] = useState({ deviceId: '', endpointId: '', logicalRole: '' });
  const [advancedDeviceId, setAdvancedDeviceId] = useState<string | null>(null);
  const selected = devices.find((device) => device.id === binding.deviceId);
  return (
    <div className="space-y-4">
      <div className="rounded-md border">
        <Table>
          <THead>
            <TR>
              <TH>{t('provision.hardware.colAsset', 'Asset')}</TH>
              <TH>{t('provision.hardware.colClass', 'Class / adapter')}</TH>
              <TH>{t('provision.hardware.colPlacement', 'Placement')}</TH>
              <TH>{t('provision.hardware.colBindings', 'Bindings')}</TH>
              <TH>{t('provision.hardware.colLifecycle', 'Lifecycle')}</TH>
              <TH>{t('provision.hardware.colHealth', 'Health')}</TH>
              <TH>{t('provision.hardware.colMode', 'Mode')}</TH>
            </TR>
          </THead>
          <TBody>
            {devices.map((device) => (
              <TR key={device.id}>
                <TD>
                  <div className="font-medium">
                    {device.serialAssetId ?? t('provision.hardware.unserialized', 'Unserialized')}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {device.vendor} {device.model}
                  </div>
                </TD>
                <TD>
                  {device.classKey}
                  <br />
                  <code className="text-xs">{device.adapterId}</code>
                </TD>
                <TD>
                  {device.endpoint?.code ?? t('provision.hardware.available', 'Available')}
                  {device.secretReference !== null && (
                    <div className="text-xs text-muted-foreground">
                      {t(
                        'provision.hardware.credentialConfigured',
                        'Credential reference configured',
                      )}
                    </div>
                  )}
                </TD>
                <TD>
                  {device.bindings.map((item) => (
                    <Badge key={item.id} variant="outline">
                      {item.logicalRole}
                      {item.required ? ` · ${t('provision.field.required', 'required')}` : ''}
                    </Badge>
                  ))}
                </TD>
                <TD>
                  <Badge variant="outline">{device.lifecycle}</Badge>
                </TD>
                <TD>
                  <Badge
                    variant={
                      device.observedHealth === 'healthy'
                        ? 'success'
                        : device.observedHealth === 'failed'
                          ? 'destructive'
                          : 'outline'
                    }
                  >
                    {device.observedHealth}
                  </Badge>
                  {device.diagnosticCode !== null && (
                    <code className="ml-1 text-xs">{device.diagnosticCode}</code>
                  )}
                </TD>
                <TD>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge
                      variant={device.catalogueMatchStatus === 'managed' ? 'success' : 'outline'}
                    >
                      {device.catalogueMatchStatus === 'custom_legacy'
                        ? t('provision.hardware.customLegacy', 'Custom/legacy')
                        : device.catalogueMatchStatus.replaceAll('_', ' ')}
                    </Badge>
                    {device.bindings.some((item) => item.catalogueMatchStatus !== 'managed') && (
                      <Badge variant="outline">
                        {t('provision.hardware.bindingCustomLegacy', 'binding Custom/legacy')}
                      </Badge>
                    )}
                    {canAdmin && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setAdvancedDeviceId(device.id)}
                      >
                        {t('provision.hardware.advancedDetail', 'Advanced/detail')}
                      </Button>
                    )}
                  </div>
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      </div>
      {advancedDeviceId !== null && (
        <AdvancedHardwarePanel
          deviceId={advancedDeviceId}
          onClose={() => setAdvancedDeviceId(null)}
        />
      )}
      {canAdmin && (
        <div className="grid gap-4 lg:grid-cols-2">
          <form
            className="space-y-2 rounded-lg border p-4"
            onSubmit={(event) => {
              event.preventDefault();
              void create
                .mutateAsync({
                  classKey: form.classKey,
                  serialAssetId: form.serialAssetId || undefined,
                  connectionType: form.connectionType,
                  connectionParameters: {},
                  adapterId: form.adapterId,
                  secretReference: form.secretReference || undefined,
                })
                .then(() => setForm({ ...form, serialAssetId: '' }));
            }}
          >
            <h2 className="font-medium">
              {t('provision.hardware.registerTitle', 'Register peripheral')}
            </h2>
            <Input
              required
              placeholder={t('provision.hardware.classKey', 'Class key')}
              value={form.classKey}
              onChange={(event) => setForm({ ...form, classKey: event.target.value })}
            />
            <Input
              placeholder={t('provision.hardware.serialAssetId', 'Serial / asset id')}
              value={form.serialAssetId}
              onChange={(event) => setForm({ ...form, serialAssetId: event.target.value })}
            />
            <Input
              required
              placeholder={t('provision.hardware.connectionType', 'Connection type')}
              value={form.connectionType}
              onChange={(event) => setForm({ ...form, connectionType: event.target.value })}
            />
            <Input
              required
              placeholder={t('provision.hardware.adapterId', 'Adapter id')}
              value={form.adapterId}
              onChange={(event) => setForm({ ...form, adapterId: event.target.value })}
            />
            <Input
              placeholder={t(
                'provision.hardware.secretReference',
                'Secret reference (never a value)',
              )}
              value={form.secretReference}
              onChange={(event) => setForm({ ...form, secretReference: event.target.value })}
            />
            <Button size="sm">{t('provision.hardware.registerDevice', 'Register device')}</Button>
          </form>
          <form
            className="space-y-2 rounded-lg border p-4"
            onSubmit={(event) => {
              event.preventDefault();
              if (selected !== undefined)
                void assign.mutateAsync({ ...binding, expectedVersion: selected.version });
            }}
          >
            <h2 className="font-medium">{t('provision.hardware.bindTitle', 'Bind peripheral')}</h2>
            <Select
              required
              value={binding.deviceId}
              onChange={(event) => setBinding({ ...binding, deviceId: event.target.value })}
            >
              <option value="">
                {t('provision.hardware.selectDevice', 'Select available device')}
              </option>
              {devices
                .filter((device) => device.lifecycle !== 'retired')
                .map((device) => (
                  <option key={device.id} value={device.id}>
                    {device.serialAssetId ?? device.id} · {device.classKey}
                  </option>
                ))}
            </Select>
            <Select
              required
              value={binding.endpointId}
              onChange={(event) => setBinding({ ...binding, endpointId: event.target.value })}
            >
              <option value="">{t('provision.field.selectEndpoint', 'Select endpoint')}</option>
              {endpoints.map((endpoint) => (
                <option key={endpoint.id} value={endpoint.id}>
                  {endpoint.code} — {endpoint.displayName}
                </option>
              ))}
            </Select>
            <Input
              required
              placeholder={t(
                'provision.hardware.logicalRole',
                'Logical role, e.g. payment_terminal',
              )}
              value={binding.logicalRole}
              onChange={(event) => setBinding({ ...binding, logicalRole: event.target.value })}
            />
            <div className="flex gap-2">
              <Button size="sm">{t('provision.hardware.assign', 'Assign')}</Button>
              {selected?.endpointId !== null && selected !== undefined && (
                <Button
                  size="sm"
                  variant="outline"
                  type="button"
                  onClick={() =>
                    lifecycle.mutate({
                      deviceId: selected.id,
                      action: 'unassign',
                      expectedVersion: selected.version,
                      reason: 'Operator unassigned from Provision hardware view',
                    })
                  }
                >
                  {t('provision.hardware.unassign', 'Unassign')}
                </Button>
              )}
              {selected !== undefined && (
                <Button
                  size="sm"
                  variant="destructive"
                  type="button"
                  onClick={() =>
                    lifecycle.mutate({
                      deviceId: selected.id,
                      action: 'retire',
                      expectedVersion: selected.version,
                      reason: 'Operator retired from Provision hardware view',
                    })
                  }
                >
                  {t('provision.hardware.retire', 'Retire')}
                </Button>
              )}
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function Overview({
  endpoints,
  nodes,
  groups,
  drift,
}: {
  endpoints: ProvisionEndpoint[];
  nodes: ProvisionNode[];
  groups: Array<{ id: string }>;
  drift?: ProvisionDriftReport;
}) {
  const t = useT('admin_ui');
  const applications = endpoints.flatMap((endpoint) => endpoint.applications);
  const inSync = drift?.counts['in_sync'] ?? 0;
  const cards = [
    [t('provision.overview.physicalNodes', 'Physical nodes'), nodes.length],
    [t('provision.overview.endpoints', 'Endpoints'), endpoints.length],
    [t('provision.field.applications', 'Applications'), applications.length],
    [t('provision.overview.operationalGroups', 'Operational groups'), groups.length],
    [t('provision.overview.inSync', 'In sync'), inSync],
    [t('provision.overview.needsAttention', 'Needs attention'), (drift?.total ?? 0) - inSync],
  ];
  return (
    <div className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {cards.map(([label, value]) => (
          <section key={label} className="rounded-lg border bg-card p-4">
            <div className="text-sm text-muted-foreground">{label}</div>
            <div className="mt-1 text-3xl font-semibold">{value}</div>
          </section>
        ))}
      </div>
      <section className="rounded-lg border p-4">
        <h2 className="font-medium">{t('provision.overview.convergence', 'Convergence state')}</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {Object.entries(drift?.counts ?? {}).map(([state, count]) => (
            <Badge key={state} variant={state === 'in_sync' ? 'success' : 'outline'}>
              {state.replaceAll('_', ' ')}: {count}
            </Badge>
          ))}
          {drift?.total === 0 && (
            <span className="text-sm text-muted-foreground">
              {t('provision.overview.noReports', 'No reports yet.')}
            </span>
          )}
        </div>
      </section>
      <section className="rounded-lg border p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="font-medium">
              {t('provision.overview.compositionEvidence', 'Composition evidence')}
            </h2>
            <p className="text-xs text-muted-foreground">
              {t(
                'provision.overview.compositionHelp',
                'Hardware, stable ports, adapters, components, dependencies, trust, and health.',
              )}
            </p>
          </div>
          <a className="text-sm font-medium text-primary hover:underline" href="?view=fleet">
            {t('provision.overview.inspectFleet', 'Inspect fleet')}
          </a>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {Object.entries(drift?.compositionCounts ?? {}).map(([state, count]) => (
            <a
              key={state}
              href={`?view=fleet&compositionState=${state}`}
              className="rounded-full focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <Badge variant={state === 'in_sync' ? 'success' : 'outline'}>
                {state.replaceAll('_', ' ')}: {count}
              </Badge>
            </a>
          ))}
        </div>
      </section>
    </div>
  );
}

function Fleet({ endpoints, drift }: { endpoints: ProvisionEndpoint[]; drift: DriftItem[] }) {
  const t = useT('admin_ui');
  const [filterParams, setFilterParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [selectedApplication, setSelectedApplication] = useState<string | null>(null);
  const desired = useDesiredStatePreview(selectedApplication);
  const parentState = filterParams.get('state') ?? '';
  const compositionState = compositionEvidenceState(filterParams.get('compositionState'));
  const diagnosticCode = filterParams.get('diagnosticCode') ?? '';
  const diagnosticCodes = Array.from(
    new Set(drift.flatMap((item) => item.composition.diagnostics.map((entry) => entry.code))),
  ).sort();
  const applicationVisible = (applicationId: string) => {
    const item = drift.find((candidate) => candidate.applicationId === applicationId);
    if (parentState !== '' && item?.state !== parentState) return false;
    if (compositionState !== '' && item?.compositionState !== compositionState) return false;
    if (
      diagnosticCode !== '' &&
      !item?.composition.diagnostics.some((diagnostic) => diagnostic.code === diagnosticCode)
    ) {
      return false;
    }
    return true;
  };
  const filtered = endpoints.filter((endpoint) => {
    const matchesSearch = `${endpoint.code} ${endpoint.displayName} ${endpoint.physicalPath.join(
      ' ',
    )} ${endpoint.applications.map((application) => application.code).join(' ')}`
      .toLowerCase()
      .includes(search.toLowerCase());
    return (
      matchesSearch &&
      endpoint.applications.some((application) => applicationVisible(application.id))
    );
  });
  function setFleetFilter(key: string, value: string) {
    const next = new URLSearchParams(filterParams);
    next.set('view', 'fleet');
    if (value === '') next.delete(key);
    else next.set(key, value);
    setFilterParams(next);
  }
  const selectedDrift = drift.find((candidate) => candidate.applicationId === selectedApplication);
  return (
    <div className="grid gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(320px,1fr)]">
      <section className="space-y-3">
        <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-4">
          <Input
            aria-label={t('provision.fleet.filterLabel', 'Filter fleet')}
            placeholder={t('provision.fleet.searchPlaceholder', 'Endpoint, path, or application')}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <Select
            aria-label={t('provision.fleet.filterStateLabel', 'Filter parent convergence state')}
            value={parentState}
            onChange={(event) => setFleetFilter('state', event.target.value)}
          >
            <option value="">{t('provision.fleet.allStates', 'All Provision states')}</option>
            {[
              'in_sync',
              'pending_sync',
              'waiting_for_idle',
              'offline',
              'failed',
              'incompatible',
              'approval_revoked',
            ].map((state) => (
              <option key={state} value={state}>
                {state.replaceAll('_', ' ')}
              </option>
            ))}
          </Select>
          <Select
            aria-label={t(
              'provision.fleet.filterCompositionLabel',
              'Filter composition evidence state',
            )}
            value={compositionState}
            onChange={(event) => setFleetFilter('compositionState', event.target.value)}
          >
            <option value="">
              {t('provision.fleet.allCompositionStates', 'All composition states')}
            </option>
            {COMPOSITION_EVIDENCE_STATES.map((state) => (
              <option key={state} value={state}>
                {state.replaceAll('_', ' ')}
              </option>
            ))}
          </Select>
          <Select
            aria-label={t('provision.fleet.filterDiagnosticLabel', 'Filter composition diagnostic')}
            value={diagnosticCode}
            onChange={(event) => setFleetFilter('diagnosticCode', event.target.value)}
          >
            <option value="">{t('provision.fleet.allDiagnostics', 'All diagnostics')}</option>
            {diagnosticCodes.map((code) => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </Select>
        </div>
        <div className="flex flex-wrap gap-2">
          {COMPOSITION_EVIDENCE_STATES.map((state) => (
            <Badge key={state} variant={state === 'in_sync' ? 'success' : 'outline'}>
              {state.replaceAll('_', ' ')}:{' '}
              {drift.filter((item) => item.compositionState === state).length}
            </Badge>
          ))}
        </div>
        <div className="rounded-md border">
          <Table>
            <THead>
              <TR>
                <TH>{t('provision.fleet.colEndpoint', 'Endpoint')}</TH>
                <TH>{t('provision.fleet.colPhysicalPath', 'Physical path')}</TH>
                <TH>{t('provision.field.applications', 'Applications')}</TH>
              </TR>
            </THead>
            <TBody>
              {filtered.map((endpoint) => (
                <TR key={endpoint.id}>
                  <TD>
                    <div className="font-medium">{endpoint.displayName}</div>
                    <code className="text-xs">{endpoint.code}</code>
                  </TD>
                  <TD>{endpoint.physicalPath.join(' / ')}</TD>
                  <TD className="space-x-1">
                    {endpoint.applications
                      .filter((application) => applicationVisible(application.id))
                      .map((application) => {
                        const item = drift.find(
                          (candidate) => candidate.applicationId === application.id,
                        );
                        return (
                          <Button
                            key={application.id}
                            size="sm"
                            variant={selectedApplication === application.id ? 'default' : 'outline'}
                            onClick={() => setSelectedApplication(application.id)}
                          >
                            {application.code} ·{' '}
                            {item?.state ?? t('provision.fleet.unreported', 'unreported')} ·{' '}
                            {item?.compositionState ?? t('provision.fleet.unknown', 'unknown')}
                          </Button>
                        );
                      })}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </div>
      </section>
      <aside className="space-y-3">
        <section className="rounded-lg border bg-card p-4">
          <h2 className="font-medium">
            {t('provision.fleet.desiredVsObserved', 'Desired vs observed')}
          </h2>
          {selectedApplication === null ? (
            <p className="mt-2 text-sm text-muted-foreground">
              {t(
                'provision.fleet.selectApplication',
                'Select an application to inspect its effective value sources.',
              )}
            </p>
          ) : (
            <div className="mt-3 space-y-3 text-sm">
              {(() => {
                return (
                  <div className="grid grid-cols-2 gap-2">
                    <span>{t('provision.fleet.observedCore', 'Observed core')}</span>
                    <code>
                      {selectedDrift?.observedCoreVersion ??
                        t('provision.fleet.offline', 'offline')}
                    </code>
                    <span>{t('provision.fleet.revision', 'Revision')}</span>
                    <code>
                      {selectedDrift?.observedRevision ?? '—'} /{' '}
                      {selectedDrift?.desiredRevision ?? '—'}{' '}
                      {t('provision.fleet.desired', 'desired')}
                    </code>
                  </div>
                );
              })()}
              <div>
                <div className="text-xs uppercase text-muted-foreground">
                  {t('provision.fleet.checksum', 'Checksum')}
                </div>
                <code className="break-all text-xs">
                  {desired.data?.checksum ?? t('provision.fleet.resolving', 'Resolving…')}
                </code>
              </div>
              {desired.data !== undefined && compositionHasCustomLegacy(desired.data.values) && (
                <Badge variant="outline">
                  {t('provision.fleet.customLegacyComposition', 'Custom/legacy composition')}
                </Badge>
              )}
              <div>
                <div className="text-xs uppercase text-muted-foreground">
                  {t('provision.fleet.sources', 'Sources')}
                </div>
                <ul className="mt-1 space-y-1">
                  {desired.data?.sources.map((source) => (
                    <li key={`${source.path}-${source.assignmentId}`}>
                      <code>{source.path}</code> ← {source.sourceType}:{source.sourceId}
                    </li>
                  ))}
                </ul>
              </div>
              {(desired.data?.conflicts.length ?? 0) > 0 && (
                <Badge variant="destructive">
                  {desired.data?.conflicts.length}{' '}
                  {t('provision.fleet.blockedConflicts', 'blocked conflicts')}
                </Badge>
              )}
            </div>
          )}
        </section>
        {selectedApplication !== null && <CompositionDriftPanel item={selectedDrift} />}
      </aside>
    </div>
  );
}

const COMPOSITION_EVIDENCE_STATES: CompositionEvidenceState[] = [
  'unknown',
  'unsupported',
  'stale',
  'mismatch',
  'pending',
  'partial',
  'in_sync',
];

function compositionEvidenceState(value: string | null): CompositionEvidenceState | '' {
  return COMPOSITION_EVIDENCE_STATES.includes(value as CompositionEvidenceState)
    ? (value as CompositionEvidenceState)
    : '';
}

export function compositionHasCustomLegacy(values: Record<string, unknown>) {
  const hardware = objectValue(values['hardware']);
  const software = objectValue(values['software']);
  const components = objectValue(software['components']);
  const manualHardware = Object.values(hardware).some((value) => {
    const binding = objectValue(value);
    return (
      typeof binding['equipmentModelRevisionId'] !== 'string' ||
      typeof binding['adapterComponentVersionId'] !== 'string'
    );
  });
  const manualSoftware = Object.values(components).some(
    (value) => typeof objectValue(value)['componentVersionId'] !== 'string',
  );
  return manualHardware || manualSoftware;
}

function objectValue(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function Topology({ levels, nodes }: { levels: ProvisionLevel[]; nodes: ProvisionNode[] }) {
  const t = useT('admin_ui');
  const canAdmin = useHasPermission('provision.admin');
  const createLevel = useCreateHierarchyLevel();
  const createNode = useCreatePhysicalNode();
  const [levelName, setLevelName] = useState('');
  const [node, setNode] = useState({ levelId: '', parentId: '', code: '', name: '' });
  const selectedLevel = levels.find((level) => level.id === node.levelId);
  const eligibleParents =
    selectedLevel === undefined || selectedLevel.position === 0
      ? []
      : nodes.filter((candidate) => {
          const candidateLevel = levels.find((level) => level.id === candidate.levelId);
          return candidateLevel?.position === selectedLevel.position - 1;
        });
  const children = (parentId: string | null) =>
    nodes.filter((candidate) => candidate.parentId === parentId);
  const tree = (parentId: string | null, depth = 0): ReactNode =>
    children(parentId).map((item) => (
      <div key={item.id}>
        <div className="border-l py-1 pl-3" style={{ marginLeft: depth * 16 }}>
          <code className="mr-2 text-xs">{item.code}</code>
          {item.name}
        </div>
        {tree(item.id, depth + 1)}
      </div>
    ));
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <section className="rounded-lg border p-4">
        <h2 className="font-medium">{t('provision.topology.title', 'Physical hierarchy')}</h2>
        <div className="mt-2 flex flex-wrap gap-2 text-xs text-muted-foreground">
          {levels.map((level) => (
            <span key={level.id} className="rounded border px-2 py-1">
              {level.position + 1}. {level.name}
              {level.systemOwned ? ` · ${t('provision.field.required', 'required')}` : ''}
            </span>
          ))}
        </div>
        <div className="mt-3">
          {tree(null)}
          {nodes.length === 0 && (
            <span className="text-sm text-muted-foreground">
              {t('provision.topology.empty', 'No physical nodes.')}
            </span>
          )}
        </div>
      </section>
      {canAdmin && (
        <section className="space-y-5 rounded-lg border p-4">
          <form
            className="space-y-2"
            onSubmit={(event) => {
              event.preventDefault();
              void createLevel
                .mutateAsync({ name: levelName, position: levels.length })
                .then(() => setLevelName(''));
            }}
          >
            <h2 className="font-medium">
              {t('provision.topology.addLevelTitle', 'Add hierarchy level')}
            </h2>
            <Input
              required
              value={levelName}
              onChange={(event) => setLevelName(event.target.value)}
              placeholder={t('provision.topology.levelPlaceholder', 'e.g. Department')}
            />
            <Button size="sm" disabled={createLevel.isPending}>
              {t('provision.topology.addLevel', 'Add level')} {levels.length + 1}
            </Button>
          </form>
          <form
            className="space-y-2"
            onSubmit={(event) => {
              event.preventDefault();
              void createNode
                .mutateAsync({
                  levelId: node.levelId,
                  ...(node.parentId ? { parentId: node.parentId } : {}),
                  code: node.code,
                  name: node.name,
                })
                .then(() => setNode({ ...node, code: '', name: '' }));
            }}
          >
            <h2 className="font-medium">
              {t('provision.topology.addNodeTitle', 'Add physical node')}
            </h2>
            <Select
              required
              value={node.levelId}
              onChange={(event) => setNode({ ...node, levelId: event.target.value, parentId: '' })}
            >
              <option value="">{t('provision.topology.selectLevel', 'Select level')}</option>
              {levels.map((level) => (
                <option key={level.id} value={level.id}>
                  {level.position + 1}. {level.name}
                </option>
              ))}
            </Select>
            <Select
              required={(selectedLevel?.position ?? 0) > 0}
              value={node.parentId}
              onChange={(event) => setNode({ ...node, parentId: event.target.value })}
            >
              <option value="">
                {selectedLevel?.position === 0
                  ? t('provision.topology.root', 'Root')
                  : t('provision.topology.selectParent', 'Select required parent')}
              </option>
              {eligibleParents.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </Select>
            <Input
              required
              value={node.code}
              onChange={(event) => setNode({ ...node, code: event.target.value })}
              placeholder={t('provision.field.code', 'Code')}
            />
            <Input
              required
              value={node.name}
              onChange={(event) => setNode({ ...node, name: event.target.value })}
              placeholder={t('provision.field.name', 'Name')}
            />
            <Button size="sm" disabled={createNode.isPending}>
              {t('provision.topology.addNode', 'Add node')}
            </Button>
          </form>
        </section>
      )}
    </div>
  );
}

function Groups({
  groups,
  endpoints,
}: {
  groups: Array<{
    id: string;
    parentId: string | null;
    name: string;
    priority: number;
    directMemberCount: number;
    effectiveMemberCount: number;
  }>;
  endpoints: ProvisionEndpoint[];
}) {
  const t = useT('admin_ui');
  const canAdmin = useHasPermission('provision.admin');
  const canWrite = useHasPermission('provision.write');
  const create = useCreateProvisionGroup();
  const membership = useProvisionGroupMembership();
  const [form, setForm] = useState({ name: '', parentId: '', priority: 0 });
  const [member, setMember] = useState({ groupId: '', endpointId: '' });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    void create
      .mutateAsync({
        name: form.name,
        ...(form.parentId ? { parentId: form.parentId } : {}),
        priority: form.priority,
      })
      .then(() => setForm({ ...form, name: '' }));
  };
  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(300px,1fr)]">
      <div className="rounded-md border">
        <Table>
          <THead>
            <TR>
              <TH>{t('provision.groups.colGroup', 'Group')}</TH>
              <TH>{t('provision.groups.colParent', 'Parent')}</TH>
              <TH>{t('provision.groups.colPriority', 'Priority')}</TH>
              <TH>{t('provision.groups.colMembers', 'Direct / effective')}</TH>
            </TR>
          </THead>
          <TBody>
            {groups.map((group) => (
              <TR key={group.id}>
                <TD className="font-medium">{group.name}</TD>
                <TD>{groups.find((parent) => parent.id === group.parentId)?.name ?? '—'}</TD>
                <TD>{group.priority}</TD>
                <TD>
                  {group.directMemberCount} / {group.effectiveMemberCount}
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      </div>
      <aside className="space-y-5">
        {canAdmin && (
          <form className="space-y-2 rounded-lg border p-4" onSubmit={submit}>
            <h2 className="font-medium">
              {t('provision.groups.createTitle', 'Create nested group')}
            </h2>
            <Input
              required
              placeholder={t('provision.field.name', 'Name')}
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
            />
            <Select
              value={form.parentId}
              onChange={(event) => setForm({ ...form, parentId: event.target.value })}
            >
              <option value="">{t('provision.groups.noParent', 'No parent')}</option>
              {groups.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </Select>
            <Input
              type="number"
              value={form.priority}
              onChange={(event) => setForm({ ...form, priority: Number(event.target.value) })}
            />
            <Button size="sm">{t('provision.groups.create', 'Create group')}</Button>
          </form>
        )}
        {canWrite && (
          <form
            className="space-y-2 rounded-lg border p-4"
            onSubmit={(event) => {
              event.preventDefault();
              void membership.mutateAsync({ ...member, add: true });
            }}
          >
            <h2 className="font-medium">
              {t('provision.groups.addMemberTitle', 'Add direct member')}
            </h2>
            <Select
              required
              value={member.groupId}
              onChange={(event) => setMember({ ...member, groupId: event.target.value })}
            >
              <option value="">{t('provision.groups.selectGroup', 'Select group')}</option>
              {groups.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </Select>
            <Select
              required
              value={member.endpointId}
              onChange={(event) => setMember({ ...member, endpointId: event.target.value })}
            >
              <option value="">{t('provision.field.selectEndpoint', 'Select endpoint')}</option>
              {endpoints.map((endpoint) => (
                <option key={endpoint.id} value={endpoint.id}>
                  {endpoint.code} — {endpoint.displayName}
                </option>
              ))}
            </Select>
            <Button size="sm">{t('provision.groups.addMember', 'Add member')}</Button>
          </form>
        )}
      </aside>
    </div>
  );
}
